import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';

// ============================================
// PORTFOLIO SPARKLINE CHART
// Beholder-style portfolio value over time
// ============================================

const ChartContainer = styled.div`
  width: 100%;
  margin: 16px 0;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 8px;
  flex-wrap: wrap;
`;

const ChangeInfo = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
`;

const ChangePercent = styled.span`
  font-weight: 500;
  white-space: nowrap;
`;

const ChangeAmount = styled.span`
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PeriodSelector = styled.div`
  display: flex;
  gap: 2px;
  background: #1a1a1a;
  border-radius: 6px;
  padding: 2px;
  flex-shrink: 0;
`;

const PeriodButton = styled.button<{ $active: boolean }>`
  padding: 4px 6px;
  font-size: 10px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 150ms ease;
  background: ${props => props.$active ? '#27272a' : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : '#71717a'};
  white-space: nowrap;
  
  &:hover {
    color: #ffffff;
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 80px;
`;

const NoDataMessage = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #52525b;
  font-size: 12px;
`;

type Period = 'D' | 'W' | 'M' | 'Y' | 'All';

interface ChartDataPoint {
  timestamp: number;
  value: number;
}

interface PortfolioChartProps {
  currentValue: number;
  tokens?: Array<{
    symbol: string;
    valueUSD: number;
    chainId: number;
  }>;
}

// Generate simulated historical data based on current value
// In production, this would fetch real historical prices from CoinGecko/Relay
const generateHistoricalData = (
  currentValue: number,
  period: Period
): ChartDataPoint[] => {
  const now = Date.now();
  const points: ChartDataPoint[] = [];
  
  // Define period parameters
  const periodConfig: Record<Period, { duration: number; interval: number; volatility: number }> = {
    'D': { duration: 24 * 60 * 60 * 1000, interval: 60 * 60 * 1000, volatility: 0.02 },      // 24h, hourly
    'W': { duration: 7 * 24 * 60 * 60 * 1000, interval: 4 * 60 * 60 * 1000, volatility: 0.05 }, // 7d, 4-hourly
    'M': { duration: 30 * 24 * 60 * 60 * 1000, interval: 24 * 60 * 60 * 1000, volatility: 0.10 }, // 30d, daily
    'Y': { duration: 365 * 24 * 60 * 60 * 1000, interval: 7 * 24 * 60 * 60 * 1000, volatility: 0.30 }, // 1y, weekly
    'All': { duration: 2 * 365 * 24 * 60 * 60 * 1000, interval: 14 * 24 * 60 * 60 * 1000, volatility: 0.50 }, // 2y, bi-weekly
  };
  
  const config = periodConfig[period];
  const startTime = now - config.duration;
  
  // Generate a smooth random walk from past to present
  // Start with a value that will end up at currentValue
  let baseValue = currentValue * (1 - (Math.random() * config.volatility * 2 - config.volatility));
  const trend = (currentValue - baseValue) / (config.duration / config.interval);
  
  let value = baseValue;
  for (let t = startTime; t <= now; t += config.interval) {
    // Add some randomness while trending toward current value
    const noise = (Math.random() - 0.5) * currentValue * (config.volatility / 10);
    value = value + trend + noise;
    
    // Ensure we don't go negative
    value = Math.max(value, currentValue * 0.1);
    
    points.push({
      timestamp: t,
      value: value,
    });
  }
  
  // Ensure the last point is exactly the current value
  if (points.length > 0) {
    points[points.length - 1].value = currentValue;
  }
  
  return points;
};

// Calculate change from historical data
const calculateChange = (data: ChartDataPoint[], currentValue: number): { percent: number; amount: number } => {
  if (data.length < 2) return { percent: 0, amount: 0 };
  
  const startValue = data[0].value;
  const amount = currentValue - startValue;
  const percent = startValue > 0 ? ((currentValue - startValue) / startValue) * 100 : 0;
  
  return { percent, amount };
};

const PortfolioChart: React.FC<PortfolioChartProps> = ({ currentValue, tokens }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('D');
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  
  // Generate chart data when period or value changes
  useEffect(() => {
    if (currentValue > 0) {
      const data = generateHistoricalData(currentValue, selectedPeriod);
      setChartData(data);
    } else {
      setChartData([]);
    }
  }, [currentValue, selectedPeriod]);
  
  const change = useMemo(() => {
    return calculateChange(chartData, currentValue);
  }, [chartData, currentValue]);
  
  const isPositive = change.percent >= 0;
  
  // Determine chart color based on performance
  const chartColor = isPositive ? '#10b981' : '#ef4444';
  const chartGradientId = `portfolioGradient-${selectedPeriod}`;
  
  if (currentValue <= 0) {
    return (
      <ChartContainer>
        <NoDataMessage>Connect wallet to see portfolio chart</NoDataMessage>
      </ChartContainer>
    );
  }
  
  return (
    <ChartContainer>
      <ChartHeader>
        <ChangeInfo $positive={isPositive}>
          <ChangePercent>
            {isPositive ? '+' : ''}{change.percent.toFixed(2)}%
          </ChangePercent>
          <ChangeAmount>
            {isPositive ? '+' : ''}${Math.abs(change.amount).toFixed(2)}
          </ChangeAmount>
        </ChangeInfo>
        
        <PeriodSelector>
          {(['D', 'W', 'M', 'Y', 'All'] as Period[]).map((period) => (
            <PeriodButton
              key={period}
              $active={selectedPeriod === period}
              onClick={() => setSelectedPeriod(period)}
            >
              {period}
            </PeriodButton>
          ))}
        </PeriodSelector>
      </ChartHeader>
      
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={chartGradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis domain={['dataMin', 'dataMax']} hide />
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={1.5}
              fill={`url(#${chartGradientId})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </ChartContainer>
  );
};

export default PortfolioChart;


